import { useEffect, useState } from 'react'
import {BookOpen, Calendar, MapPin, Users, Clock, Tag, ExternalLink, CheckCircle, Search} from 'lucide-react'
import ColaboradorLayout from '@/components/ColaboradorLayout'
import { useTrainings, TRAINING_TYPE_LABELS, TRAINING_MODALITY_LABELS, ENROLLMENT_STATUS_META, type Training } from '@/hooks/useTrainings'
import { useNotifications } from '@/hooks/useNotifications'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'

const TYPE_COLORS: Record<string, string> = {
  curso: 'bg-blue-100 text-blue-700',
  taller: 'bg-violet-100 text-violet-700',
  seminario: 'bg-emerald-100 text-emerald-700',
  certificacion: 'bg-amber-100 text-amber-700',
}

function ColaboradorCapacitaciones() {
  const { user } = useAuth()
  const { trainings, enrollments, loading, error, fetchPublished, fetchMyEnrollments, enroll } = useTrainings()
  const { notifyUser } = useNotifications()
  const [collaboratorName, setCollaboratorName] = useState<string>('')
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState<string>('todos')
  const [filterModality, setFilterModality] = useState<string>('todos')
  const [tab, setTab] = useState<'catalogo' | 'mis-inscripciones'>('catalogo')
  const [enrolling, setEnrolling] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [selected, setSelected] = useState<Training | null>(null)

  useEffect(() => {
    fetchPublished()
    if (user?.userId) {
      fetchMyEnrollments(user.userId)
      // Carga el nombre completo del colaborador para usarlo en inscripciones y notificaciones
      lumi.entities.collaborators
        .list({ filter: { userId: user.userId }, limit: 1 })
        .then((res) => {
          const prof = res.list?.[0] as { fullName?: string } | undefined
          if (prof?.fullName) setCollaboratorName(prof.fullName)
        })
        .catch((e) => console.error('[ColaboradorCapacitaciones] perfil error:', e))
    }
  }, [])

  const enrolledIds = new Set(enrollments.map((e) => e.trainingId))

  const filtered = trainings.filter((t) => {
    const matchSearch =
      !search ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.instructor?.toLowerCase().includes(search.toLowerCase()) ||
      t.tags?.some((tag) => tag.toLowerCase().includes(search.toLowerCase()))
    const matchType = filterType === 'todos' || t.type === filterType
    const matchModality = filterModality === 'todos' || t.modality === filterModality
    return matchSearch && matchType && matchModality
  })

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const handleEnroll = async (training: Training) => {
    if (!user?.userId) return
    setEnrolling(training._id)
    // Nombre identificable del colaborador (nunca el usuario del correo)
    const displayName = collaboratorName || user.userName || 'Colaborador'
    try {
      await enroll(training, user.userId, displayName)
      await fetchMyEnrollments(user.userId)
      // Notifica al administrador para que gestione la nueva inscripción del curso
      await notifyUser({
        targetUserId: 'admin',
        title: 'Nueva inscripción a capacitación',
        body: `${displayName} se inscribió en "${training.title}". Gestiona el curso desde el panel de capacitaciones.`,
        type: 'curso',
        audience: 'todos',
        linkUrl: '/admin/capacitaciones',
        creator: user.userId,
      })
      showToast(`Te inscribiste a "${training.title}" correctamente.`)
      setSelected(null)
    } catch (e) {
      console.error(e)
      showToast('No se pudo completar la inscripción. Intenta de nuevo.')
    } finally {
      setEnrolling(null)
    }
  }

  return (
    <ColaboradorLayout
      title="Capacitaciones"
      subtitle="Cursos, talleres, seminarios y certificaciones para impulsar tu carrera"
    >
      {/* Toast */}
      {toast ? (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm text-white shadow-lg">
          <CheckCircle size={16} className="text-emerald-400" />
          {toast}
        </div>
      ) : null}

      {/* Tabs */}
      <div className="mb-5 flex gap-1 rounded-xl border border-slate-200 bg-white p-1 w-fit">
        {(['catalogo', 'mis-inscripciones'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
              tab === t ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {t === 'catalogo' ? 'Catálogo' : `Mis inscripciones (${enrollments.length})`}
          </button>
        ))}
      </div>

      {tab === 'catalogo' ? (
        <>
          {/* Filtros */}
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por título, instructor o etiqueta…"
                className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400"
            >
              <option value="todos">Todos los tipos</option>
              {Object.entries(TRAINING_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select
              value={filterModality}
              onChange={(e) => setFilterModality(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400"
            >
              <option value="todos">Todas las modalidades</option>
              {Object.entries(TRAINING_MODALITY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          {error ? (
            <p className="rounded-lg bg-red-50 p-4 text-sm text-red-600">{error}</p>
          ) : loading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-52 animate-pulse rounded-xl bg-slate-100" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-16 text-center">
              <BookOpen size={32} className="mb-3 text-slate-300" />
              <p className="font-semibold text-slate-600">Sin resultados</p>
              <p className="mt-1 text-sm text-slate-400">Prueba con otros filtros o términos de búsqueda.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((t) => {
                const alreadyEnrolled = enrolledIds.has(t._id)
                return (
                  <div
                    key={t._id}
                    onClick={() => setSelected(t)}
                    className="flex cursor-pointer flex-col rounded-xl border border-slate-200 bg-white p-5 transition hover:border-amber-300 hover:shadow-sm"
                  >
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${TYPE_COLORS[t.type] ?? 'bg-slate-100 text-slate-600'}`}>
                        {TRAINING_TYPE_LABELS[t.type]}
                      </span>
                      {alreadyEnrolled ? (
                        <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
                          <CheckCircle size={13} /> Inscrito
                        </span>
                      ) : null}
                    </div>
                    <h3 className="mb-1 flex-1 text-sm font-semibold text-slate-900 leading-snug">{t.title}</h3>
                    <p className="mb-3 line-clamp-2 text-xs text-slate-500">{t.description}</p>
                    <div className="mt-auto space-y-1 text-xs text-slate-500">
                      {t.instructor ? (
                        <p className="flex items-center gap-1.5"><Users size={12} /> {t.instructor}</p>
                      ) : null}
                      {t.modality ? (
                        <p className="flex items-center gap-1.5"><MapPin size={12} /> {TRAINING_MODALITY_LABELS[t.modality]}</p>
                      ) : null}
                      {t.duration ? (
                        <p className="flex items-center gap-1.5"><Clock size={12} /> {t.duration}</p>
                      ) : null}
                      {t.startDate ? (
                        <p className="flex items-center gap-1.5"><Calendar size={12} /> {t.startDate.slice(0, 10)}</p>
                      ) : null}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-sm font-bold text-slate-900">
                        {t.cost === 0 ? 'Gratuito' : `$${t.cost?.toLocaleString('es-MX')} MXN`}
                      </span>
                      <span className="text-xs text-slate-400">{t.enrolledCount ?? 0} inscritos</span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      ) : (
        /* Mis inscripciones */
        <div className="space-y-3">
          {enrollments.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-16 text-center">
              <BookOpen size={32} className="mb-3 text-slate-300" />
              <p className="font-semibold text-slate-600">Sin inscripciones aún</p>
              <p className="mt-1 text-sm text-slate-400">Explora el catálogo y regístrate en una capacitación.</p>
            </div>
          ) : (
            enrollments.map((e) => {
              const meta = ENROLLMENT_STATUS_META[e.status]
              return (
                <div key={e._id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{e.trainingTitle ?? 'Capacitación'}</p>
                    <p className="text-xs text-slate-500">{TRAINING_TYPE_LABELS[e.trainingType as Training['type']] ?? e.trainingType} · Inscrito el {e.createdAt.slice(0, 10)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {e.certificateUrl ? (
                      <a href={e.certificateUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-amber-600 hover:underline">
                        <ExternalLink size={12} /> Certificado
                      </a>
                    ) : null}
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.color}`}>{meta.label}</span>
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}

      {/* Modal detalle / inscripción */}
      {selected ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <span className={`mb-2 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${TYPE_COLORS[selected.type] ?? ''}`}>
                  {TRAINING_TYPE_LABELS[selected.type]}
                </span>
                <h2 className="text-lg font-bold text-slate-900">{selected.title}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="shrink-0 text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <p className="mb-4 text-sm text-slate-600 leading-relaxed">{selected.description}</p>
            <div className="mb-5 grid grid-cols-2 gap-2 text-xs text-slate-500">
              {selected.instructor ? <p className="flex items-center gap-1.5"><Users size={12} /> {selected.instructor}</p> : null}
              {selected.modality ? <p className="flex items-center gap-1.5"><MapPin size={12} /> {TRAINING_MODALITY_LABELS[selected.modality]}</p> : null}
              {selected.duration ? <p className="flex items-center gap-1.5"><Clock size={12} /> {selected.duration}</p> : null}
              {selected.startDate ? <p className="flex items-center gap-1.5"><Calendar size={12} /> Inicio: {selected.startDate.slice(0, 10)}</p> : null}
              {selected.endDate ? <p className="flex items-center gap-1.5"><Calendar size={12} /> Fin: {selected.endDate.slice(0, 10)}</p> : null}
              {selected.location ? <p className="flex items-center gap-1.5"><MapPin size={12} /> {selected.location}</p> : null}
              {selected.capacity ? <p className="flex items-center gap-1.5"><Users size={12} /> Cupo: {selected.capacity}</p> : null}
            </div>
            {selected.tags && selected.tags.length > 0 ? (
              <div className="mb-5 flex flex-wrap gap-1.5">
                {selected.tags.map((tag) => (
                  <span key={tag} className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-600">
                    <Tag size={10} /> {tag}
                  </span>
                ))}
              </div>
            ) : null}
            <div className="flex items-center justify-between">
              <p className="text-lg font-bold text-slate-900">
                {selected.cost === 0 ? 'Gratuito' : `$${selected.cost?.toLocaleString('es-MX')} MXN`}
              </p>
              {enrolledIds.has(selected._id) ? (
                <span className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700">
                  <CheckCircle size={15} /> Ya estás inscrito
                </span>
              ) : (
                <button
                  onClick={() => handleEnroll(selected)}
                  disabled={enrolling === selected._id}
                  className="rounded-lg bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-amber-500 disabled:opacity-60"
                >
                  {enrolling === selected._id ? 'Inscribiendo…' : 'Inscribirme'}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </ColaboradorLayout>
  )
}

export default ColaboradorCapacitaciones
